import * as React from "react";
import { useIntl } from "react-intl";
import { Path, joinPath } from "../../utils/url";
import { useNavigate } from "react-router";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbLink,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbList,
} from "@/kit/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/kit/ui/dropdown-menu";
import { Button } from "@/kit/ui/button";
import { cn } from "@/utils/tailwind";

interface Props {
  root: Path;
  path: string;
  delimiter?: string;
}

interface PathPart {
  name: string;
  path: string;
}

interface PathBreadcrumbItemProps extends React.PropsWithChildren {
  active?: boolean;
  to: string;
}

const PathBreadcrumbItem = ({
  to,
  active = false,
  children,
}: PathBreadcrumbItemProps) => {
  const navigate = useNavigate();
  return (
    <BreadcrumbLink asChild>
      <Button
        size="sm"
        variant="tertiary"
        hue="grey"
        onClick={() => navigate(to)}
        className={cn(!active && "text-grey-600 font-medium")}
      >
        {children}
      </Button>
    </BreadcrumbLink>
  );
};

const PathBreadcrumbEllipses = ({
  parts,
  root,
}: {
  parts: PathPart[];
  root: Path;
}) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="cursor-pointer">
        <BreadcrumbEllipsis />
        <span className="sr-only">Toggle path menu</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {parts.map((part, index) => (
          <DropdownMenuItem
            key={index}
            onClick={() => navigate(joinPath(root, part.path))}
          >
            {part.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function PathBreadcrumbs({ path, root }: Props) {
  const intl = useIntl();

  const parts: PathPart[] = path.split("/").map((part, i, arr) => ({
    name: part,
    path: "/tree/" + arr.slice(0, i + 1).join("/"),
  }));

  const rootPart: PathPart = {
    name: intl.formatMessage({ defaultMessage: "root" }),
    path: "",
  };

  const allParts = [rootPart, ...parts];
  const last = allParts[allParts.length - 1];
  const middle = allParts.slice(1, -1);
  const isRootLast = allParts.length === 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <PathBreadcrumbItem
            to={joinPath(root, rootPart.path).pathname}
            active={isRootLast}
          >
            {rootPart.name}
          </PathBreadcrumbItem>
        </BreadcrumbItem>

        {middle.length > 0 && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {middle.length > 1 ? (
                <PathBreadcrumbEllipses parts={middle} root={root} />
              ) : (
                <PathBreadcrumbItem
                  to={joinPath(root, middle[0].path).pathname}
                >
                  {middle[0].name}
                </PathBreadcrumbItem>
              )}
            </BreadcrumbItem>
          </>
        )}

        {!isRootLast && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <PathBreadcrumbItem
                to={joinPath(root, last.path).pathname}
                active
              >
                {last.name}
              </PathBreadcrumbItem>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
